import * as React from 'react';
import { createContext, useCallback, useContext } from 'react';
import type { ReactElement } from 'react';

import type { Partner_Partner as UpsellPartner } from '__generated__/graphql-types-do-not-use';

import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils/DateTimeUtils';

import type {
  Accordion as AccordionTrackingData,
  ButtonName,
  LinkType,
  PageTabName,
  SectionName,
} from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { isExternalLink } from 'bundles/common/utils/urlUtils';
import { getIsValidDeadlineCardDate, productVariantToEventing } from 'bundles/degree-description/utils/utils';

type ProductCardEventingInfo = {
  index: number;
  productId: string;
  productVariant: string;
  name?: string | null;
  partners?: Array<UpsellPartner | null>;
  slug?: string | null;
  sectionName?: SectionName;
  trackingIndex?: number;
};

type EventingProps = {
  trackClickButton: (
    buttonName: ButtonName,
    sectionName: SectionName,
    linkURL?: string | null,
    linkName?: string | null,
    trackingIndex?: number,
    linkTypeOverride?: LinkType
  ) => void;
  trackClickTestimonialCard: (
    text: string,
    author: string,
    testimonialAction: 'play_video' | 'exit_video_modal',
    authorTitle?: string | null,
    index?: number
  ) => void;
  trackClickPageTab: (name: PageTabName) => void;
  trackAccordion: (expanded: boolean, accordionTrackingData: AccordionTrackingData, sectionName?: SectionName) => void;
  trackClickDeadlineCard: (
    title?: string | null,
    enrollStartDate?: string | null,
    enrollEndDate?: string | null,
    courseStartDate?: string | null
  ) => void;
  trackViewDescriptionPage: () => void;
  trackClickProductCard: (eventingInfo: ProductCardEventingInfo) => void;
  useGetViewPageSectionRef: (
    sectionName: SectionName,
    trackingIndex?: number
  ) => React.MutableRefObject<HTMLDivElement | null>;
  useGetViewDeadlineCardRef: (
    title?: string | null,
    enrollStartDate?: string | null,
    enrollEndDate?: string | null,
    courseStartDate?: string | null
  ) => React.MutableRefObject<HTMLDivElement | null>;
  useGetViewPathwaysCardRef: (
    linkName?: string | null,
    linkURL?: string | null
  ) => React.MutableRefObject<HTMLAnchorElement | null>;
  useGetViewTestimonialCardRef: (
    text: string,
    author: string,
    authorTitle?: string | null,
    index?: number
  ) => React.MutableRefObject<HTMLDivElement | null>;
  useGetViewProductCardRef: (eventingInfo: ProductCardEventingInfo) => React.MutableRefObject<HTMLDivElement | null>;
  useGetViewPersonalizedMessageRef: (messageId: string) => React.MutableRefObject<HTMLDivElement | null>;
  trackingProductId: string;
};

const DegreeDescriptionEventingContext = createContext<EventingProps>({
  trackClickButton: () => undefined,
  trackClickTestimonialCard: () => undefined,
  trackClickPageTab: () => undefined,
  trackAccordion: () => undefined,
  trackClickDeadlineCard: () => undefined,
  trackViewDescriptionPage: () => undefined,
  trackClickProductCard: () => undefined,
  useGetViewPageSectionRef: () => ({ current: null }),
  useGetViewDeadlineCardRef: () => ({ current: null }),
  useGetViewPathwaysCardRef: () => ({ current: null }),
  useGetViewTestimonialCardRef: () => ({ current: null }),
  useGetViewProductCardRef: () => ({ current: null }),
  useGetViewPersonalizedMessageRef: () => ({ current: null }),
  trackingProductId: '',
});

export const useDegreeDescriptionEventingContext = () => useContext(DegreeDescriptionEventingContext);

type PropsToComponent = {
  children: ReactElement;
  degreeId: string;
};

export const DegreeDescriptionEventingContextProvider = (props: PropsToComponent) => {
  const { children, degreeId } = props;
  const trackingProductId = degreeId.replace(/^base~/, '');
  const track = useTracker();

  const trackClickButton = (
    buttonName: ButtonName,
    sectionName: SectionName,
    linkURL?: string | null,
    linkName?: string | null,
    trackingIndex?: number,
    linkTypeOverride?: LinkType
  ) => {
    let linkType: LinkType | null = null;
    if (linkTypeOverride) {
      linkType = linkTypeOverride;
    } else if (linkURL) {
      linkType = isExternalLink(linkURL) ? 'external' : 'internal';
    }
    track('click_button', {
      button: {
        name: buttonName,
        ...(linkURL && { linkURL }),
        ...(linkType && { linkType }),
        ...(linkName && { linkName }),
      },
      pageSection: {
        sectionName,
        ...(trackingIndex && {
          subindex: trackingIndex,
        }),
      },
      product: {
        id: trackingProductId,
        type: 'degree',
      },
    });
  };

  const trackClickTestimonialCard = (
    text: string,
    author: string,
    testimonialAction: 'play_video' | 'exit_video_modal',
    authorTitle?: string | null,
    index?: number
  ) => {
    track('click_testimonial_card', {
      testimonialCard: {
        text,
        author,
        ...(authorTitle && {
          authorTitle,
        }),
        ...(index && {
          index,
        }),
      },
      testimonialAction,
      product: {
        id: trackingProductId,
        type: 'degree',
      },
    });
  };

  const trackAccordion = (
    expanded: boolean,
    accordionTrackingData: AccordionTrackingData,
    sectionName?: SectionName
  ) => {
    track(expanded ? 'expand_accordion' : 'collapse_accordion', {
      accordion: accordionTrackingData,
      ...(sectionName && {
        pageSection: {
          sectionName,
        },
      }),

      product: {
        id: trackingProductId,
        type: 'degree',
      },
    });
  };

  const trackClickDeadlineCard = (
    title?: string | null,
    enrollStartDate?: string | null,
    enrollEndDate?: string | null,
    courseStartDate?: string | null
  ) => {
    const deadlineCardDefaultTitle = 'deadline card';
    track('click_deadline_card', {
      deadlineCard: {
        title: title ?? deadlineCardDefaultTitle,
        ...(getIsValidDeadlineCardDate(enrollStartDate) && {
          enrollStartDate: Number(formatDateTimeDisplay(enrollStartDate!, MOMENT_FORMATS.TIMESTAMP)),
        }),
        ...(getIsValidDeadlineCardDate(enrollEndDate) && {
          enrollEndDate: Number(formatDateTimeDisplay(enrollEndDate!, MOMENT_FORMATS.TIMESTAMP)),
        }),
        ...(getIsValidDeadlineCardDate(courseStartDate) && {
          courseStartDate: Number(formatDateTimeDisplay(courseStartDate!, MOMENT_FORMATS.TIMESTAMP)),
        }),
      },
      product: {
        id: trackingProductId,
        type: 'degree',
      },
    });
  };

  const trackViewDescriptionPage = useCallback<() => void>(() => {
    track('view_description_page', {
      descriptionPageApp: 'degree_description',
      product: {
        id: trackingProductId,
        type: 'degree',
      },
    });
  }, [track, trackingProductId]);

  const trackClickPageTab = (name: PageTabName) => {
    track('click_page_tab', {
      pageTab: { name },
      product: {
        id: trackingProductId,
        type: 'degree',
      },
    });
  };

  const useGetViewPageSectionRef = (
    sectionName: SectionName,
    trackingIndex?: number
  ): React.MutableRefObject<HTMLDivElement | null> => {
    return useVisibilityTracker('view_page_section', {
      pageSection: {
        sectionName,
        ...(trackingIndex && {
          subindex: trackingIndex,
        }),
      },
      product: {
        id: trackingProductId,
        type: 'degree',
      },
    });
  };

  const useGetViewDeadlineCardRef = (
    title?: string | null,
    enrollStartDate?: string | null,
    enrollEndDate?: string | null,
    courseStartDate?: string | null
  ): React.MutableRefObject<HTMLDivElement | null> => {
    const deadlineCardDefaultTitle = 'deadline card';
    return useVisibilityTracker('view_deadline_card', {
      deadlineCard: {
        title: title ?? deadlineCardDefaultTitle,
        ...(getIsValidDeadlineCardDate(enrollStartDate) && {
          enrollStartDate: Number(formatDateTimeDisplay(enrollStartDate!, MOMENT_FORMATS.TIMESTAMP)),
        }),
        ...(getIsValidDeadlineCardDate(enrollEndDate) && {
          enrollEndDate: Number(formatDateTimeDisplay(enrollEndDate!, MOMENT_FORMATS.TIMESTAMP)),
        }),
        ...(getIsValidDeadlineCardDate(courseStartDate) && {
          courseStartDate: Number(formatDateTimeDisplay(courseStartDate!, MOMENT_FORMATS.TIMESTAMP)),
        }),
      },
      product: {
        id: trackingProductId,
        type: 'degree',
      },
    });
  };

  const useGetViewPathwaysCardRef = (
    linkName?: string | null,
    linkURL?: string | null
  ): React.MutableRefObject<HTMLAnchorElement | null> => {
    let linkType: LinkType | null = null;
    if (linkURL) {
      linkType = isExternalLink(linkURL) ? 'external' : 'internal';
    }
    return useVisibilityTracker('view_button', {
      button: {
        name: 'degree_pathways_card',
        ...(linkName && {
          linkName,
        }),
        ...(linkURL && {
          linkURL,
        }),
        ...(linkType && {
          linkType,
        }),
      },
      pageSection: {
        sectionName: 'page_body',
      },
      product: {
        id: trackingProductId,
        type: 'degree',
      },
    });
  };

  const useGetViewTestimonialCardRef = (
    text: string,
    author: string,
    authorTitle?: string | null,
    index?: number
  ): React.MutableRefObject<HTMLDivElement | null> => {
    return useVisibilityTracker('view_testimonial_card', {
      testimonialCard: {
        text,
        author,
        ...(authorTitle && {
          authorTitle,
        }),
        ...(index && {
          index,
        }),
      },
      product: {
        id: trackingProductId,
        type: 'degree',
      },
    });
  };

  const getProductCardEventingData = (eventingInfo: ProductCardEventingInfo) => {
    const trackingProductVariant = productVariantToEventing(eventingInfo.productVariant);

    return {
      productCard: {
        index: eventingInfo.index,
      },
      product: {
        id: eventingInfo.productId,
        ...(trackingProductVariant && {
          type: trackingProductVariant,
        }),
        ...(eventingInfo.name && {
          name: eventingInfo.name,
        }),
        ...(eventingInfo.partners &&
          eventingInfo.partners?.[0] && {
            associatedPartner: {
              primaryPartnerId: eventingInfo.partners[0].id,
              ...(eventingInfo.partners?.[1] && {
                secondaryPartnerId: eventingInfo.partners[1].id,
              }),
            },
          }),
        ...(eventingInfo.slug && {
          slug: eventingInfo.slug,
        }),
      },
      ...(eventingInfo.sectionName && {
        pageSection: {
          sectionName: eventingInfo.sectionName,
          ...(eventingInfo.trackingIndex && {
            subindex: eventingInfo.trackingIndex,
          }),
        },
      }),
    };
  };

  const useGetViewProductCardRef = (
    eventingInfo: ProductCardEventingInfo
  ): React.MutableRefObject<HTMLDivElement | null> => {
    const eventingData = getProductCardEventingData(eventingInfo);
    return useVisibilityTracker('view_product_card', eventingData);
  };

  const trackClickProductCard = (eventingInfo: ProductCardEventingInfo) => {
    const eventingData = getProductCardEventingData(eventingInfo);
    track('click_product_card', eventingData);
  };

  const useGetViewPersonalizedMessageRef = (messageId: string): React.MutableRefObject<HTMLDivElement | null> => {
    return useVisibilityTracker('view_personalized_message', {
      personalizedMessage: {
        messageId,
      },
      product: {
        id: trackingProductId,
        type: 'degree',
      },
    });
  };

  return (
    <DegreeDescriptionEventingContext.Provider
      value={{
        trackClickButton,
        trackClickTestimonialCard,
        trackClickPageTab,
        trackAccordion,
        trackClickDeadlineCard,
        trackViewDescriptionPage,
        trackClickProductCard,
        useGetViewPageSectionRef,
        useGetViewDeadlineCardRef,
        useGetViewPathwaysCardRef,
        useGetViewTestimonialCardRef,
        useGetViewProductCardRef,
        useGetViewPersonalizedMessageRef,
        trackingProductId,
      }}
    >
      {children}
    </DegreeDescriptionEventingContext.Provider>
  );
};
